import { computed, reactive, ref, type Ref } from 'vue'

import type { RuntimeConfig } from '@/config'
import { describeError, logger } from '@/lib/log'
import { nextIndex, resolveSlot } from '@/lib/schedule'
import type { LayerApi, LayerStatus, MediaItem, Playlist } from '@/types'

export type LayerSlot = 0 | 1

/** Extra time after a crossfade before the outgoing layer is torn down. */
const RETIRE_BUFFER_MS = 80
/** How long a dropped item is kept out of the rotation before it is retried. */
const FAILURE_RETRY_MS = 5 * 60_000
/** Shorter retry window when nothing at all is playable. */
const TOTAL_FAILURE_RETRY_MS = 15_000
/** Floor on a scheduled slot so slow loads can never cause swap thrashing. */
const MIN_SLOT_MS = 1_000
const RECOVERY_WINDOW_MS = 10 * 60_000
const MAX_RECOVERIES = 4

export interface PlayerSnapshot {
  activeSlot: LayerSlot
  currentIndex: number | null
  currentItemId: string | null
  expectedIndex: number | null
  itemCount: number
  scheduledCount: number
  cycleMs: number
  transitionMs: number
  failedIds: string[]
  recoveries: number
  layers: [LayerStatus | null, LayerStatus | null]
}

export interface UsePlayerOptions {
  config: RuntimeConfig
  playlist: Ref<Playlist | null>
  getLayer: (slot: LayerSlot) => LayerApi | null
}

/**
 * Drives two playback layers so that a new item is always fully decoded and
 * playing *before* it becomes visible, and the outgoing item stays opaque
 * underneath until the crossfade has finished.
 */
export function usePlayer({ config, playlist, getLayer }: UsePlayerOptions) {
  const activeSlot = ref<LayerSlot>(0)
  const retiringSlot = ref<LayerSlot | null>(null)
  const transitionMs = ref(0)
  const currentIndex = ref<number | null>(null)
  const failedIds = reactive(new Set<string>())

  /** Anchoring on the Unix epoch keeps independent screens on the same item. */
  const epochMs = config.sync ? 0 : Date.now()

  const scheduleItems = computed<MediaItem[]>(() =>
    (playlist.value?.items ?? []).filter((item) => !failedIds.has(item.id)),
  )
  const hasContent = computed(() => currentIndex.value !== null && scheduleItems.value.length > 0)

  let running = false
  let chain: Promise<void> = Promise.resolve()
  let boundaryTimer = 0
  let retireTimer = 0
  let healthTimer = 0
  let preparedIndex: number | null = null
  let preparedSlot: LayerSlot | null = null
  let driftTicks = 0
  let lastMediaTimeMs = 0
  let lastProgressAt = Date.now()
  let lastFailureResetAt = Date.now()
  let recoveries: number[] = []

  /** Serialises every state change so two swaps can never interleave. */
  function enqueue(task: () => Promise<void>): void {
    chain = chain.then(task).catch((caught: unknown) => {
      logger.error(`Player task failed: ${describeError(caught)}`)
    })
  }

  function otherSlot(slot: LayerSlot): LayerSlot {
    return slot === 0 ? 1 : 0
  }

  function effectiveTransitionMs(item: MediaItem): number {
    const requested = config.transitionOverrideMs ?? item.transitionMs
    return Math.max(0, Math.min(requested, Math.floor(item.durationMs / 2)))
  }

  /**
   * A playlist with a single video never reaches a boundary, so nothing would
   * ever restart it — it has to loop itself. This is runtime state on purpose:
   * a longer playlist can shrink to one item when the others fail to load.
   */
  function shouldLoop(item: MediaItem, itemCount: number): boolean {
    return item.loop || (item.kind === 'video' && itemCount === 1)
  }

  function withEffectiveLoop(item: MediaItem, itemCount: number): MediaItem {
    const loop = shouldLoop(item, itemCount)
    return loop === item.loop ? item : { ...item, loop }
  }

  function scheduleBoundary(at: number): void {
    window.clearTimeout(boundaryTimer)
    const delay = Math.max(MIN_SLOT_MS, at - Date.now())
    boundaryTimer = window.setTimeout(() => {
      enqueue(() => syncToNow(true))
    }, delay)
  }

  function retire(slot: LayerSlot, delayMs: number): void {
    window.clearTimeout(retireTimer)
    retireTimer = window.setTimeout(() => {
      if (retiringSlot.value === slot) retiringSlot.value = null
      getLayer(slot)?.release()
      preloadNext()
    }, delayMs + RETIRE_BUFFER_MS)
  }

  /**
   * Loads the upcoming item into the idle layer well ahead of the boundary.
   * Queued on the same chain as swaps so a boundary can never race a preload.
   */
  function preloadNext(): void {
    const items = scheduleItems.value
    const index = currentIndex.value
    if (index === null || items.length < 2) return

    const upcoming = nextIndex(items.length, index)
    const item = items[upcoming]
    const target = otherSlot(activeSlot.value)
    const layer = getLayer(target)
    if (!item || !layer) return

    preparedIndex = null
    preparedSlot = target
    enqueue(async () => {
      try {
        await layer.prepare(withEffectiveLoop(item, items.length), 0)
        preparedIndex = upcoming
      } catch (caught) {
        // Not fatal: the boundary will retry and drop the item only if it fails again.
        logger.warn(`Preload failed for "${item.id}": ${describeError(caught)}`)
      }
    })
  }

  async function show(item: MediaItem, index: number, seekMs: number, fade: boolean): Promise<void> {
    const target = otherSlot(activeSlot.value)
    const layer = getLayer(target)
    if (!layer) throw new Error('Playback layer is not mounted')

    const preloaded = layer.status()
    const usePreloaded =
      preparedIndex === index &&
      preparedSlot === target &&
      preloaded.ready &&
      preloaded.itemId === item.id &&
      seekMs <= 300 &&
      // A shrunken playlist changes whether the item has to loop, so re-prepare.
      scheduleItems.value.length > 1

    if (!usePreloaded) {
      try {
        await layer.prepare(withEffectiveLoop(item, scheduleItems.value.length), seekMs)
      } catch (caught) {
        logger.error(`Dropping "${item.id}" from rotation: ${describeError(caught)}`)
        failedIds.add(item.id)
        preparedIndex = null
        await syncToNow(fade)
        return
      }
    }
    preparedIndex = null

    await layer.beginPlayback()

    const outgoing = activeSlot.value
    const outgoingHasContent = getLayer(outgoing)?.status().itemId !== null
    transitionMs.value = fade ? effectiveTransitionMs(item) : 0
    retiringSlot.value = outgoingHasContent ? outgoing : null
    activeSlot.value = target
    currentIndex.value = index
    lastMediaTimeMs = 0
    lastProgressAt = Date.now()
    retire(outgoing, transitionMs.value)
  }

  /**
   * Brings the screen in line with what the wall clock says should be playing.
   * Called on boundaries, on recovery, when the tab becomes visible again and
   * whenever the playlist changes — it is idempotent by design.
   */
  async function syncToNow(fade: boolean): Promise<void> {
    if (!running) return

    const items = scheduleItems.value
    if (items.length === 0) {
      currentIndex.value = null
      return
    }

    const slot = resolveSlot(items, Date.now(), epochMs)
    const item = slot ? items[slot.index] : undefined
    if (!slot || !item) return

    const active = getLayer(activeSlot.value)?.status() ?? null
    const alreadyShowing =
      currentIndex.value === slot.index && active?.itemId === item.id && active.error === null

    if (!alreadyShowing) {
      await show(item, slot.index, slot.offsetMs, fade)
    }

    const settled = resolveSlot(scheduleItems.value, Date.now(), epochMs)
    scheduleBoundary(settled?.endsAt ?? Date.now() + item.durationMs)
  }

  function recover(reason: string): void {
    const now = Date.now()
    recoveries = recoveries.filter((at) => now - at < RECOVERY_WINDOW_MS)
    recoveries.push(now)
    lastProgressAt = now

    if (recoveries.length >= MAX_RECOVERIES) {
      logger.error(`Playback keeps failing (${reason}) — reloading the page`)
      window.location.reload()
      return
    }

    logger.warn(`Recovering playback (${reason}); attempt ${recoveries.length}`)
    enqueue(async () => {
      currentIndex.value = null
      await syncToNow(false)
    })
  }

  function maybeResetFailures(now: number): void {
    if (failedIds.size === 0) return
    const retryAfterMs =
      scheduleItems.value.length === 0 ? TOTAL_FAILURE_RETRY_MS : FAILURE_RETRY_MS
    if (now - lastFailureResetAt < retryAfterMs) return
    logger.info(`Retrying ${failedIds.size} previously failed item(s)`)
    failedIds.clear()
    lastFailureResetAt = now
    enqueue(() => syncToNow(true))
  }

  function healthCheck(): void {
    const now = Date.now()
    maybeResetFailures(now)

    const items = scheduleItems.value
    if (items.length === 0) return

    const expected = resolveSlot(items, now, epochMs)
    if (expected && currentIndex.value !== null && expected.index !== currentIndex.value) {
      driftTicks += 1
      if (driftTicks >= 2) {
        driftTicks = 0
        logger.warn(`Out of sync: expected item ${expected.index}, showing ${currentIndex.value}`)
        enqueue(() => syncToNow(true))
      }
    } else {
      driftTicks = 0
    }

    const status = getLayer(activeSlot.value)?.status() ?? null
    const activeItem = currentIndex.value === null ? undefined : items[currentIndex.value]
    if (!status || status.kind !== 'video' || status.itemId === null) {
      lastMediaTimeMs = 0
      lastProgressAt = now
      return
    }
    if (status.ended && activeItem && !shouldLoop(activeItem, items.length)) {
      // Short clip parked on its last frame — expected, not a stall.
      lastProgressAt = now
      return
    }
    // A wrap-around reports a lower time; that is progress too.
    if (status.currentTimeMs > lastMediaTimeMs + 40 || status.currentTimeMs < lastMediaTimeMs) {
      lastMediaTimeMs = status.currentTimeMs
      lastProgressAt = now
      return
    }
    if (now - lastProgressAt > config.stallTimeoutMs) {
      recover(status.paused ? 'video is paused' : 'video stopped progressing')
    }
  }

  function onWake(): void {
    if (document.visibilityState !== 'visible') return
    // Background tabs get their timers throttled, so re-derive from the clock.
    enqueue(() => syncToNow(true))
  }

  function snapshot(): PlayerSnapshot {
    const items = scheduleItems.value
    const expected = resolveSlot(items, Date.now(), epochMs)
    const current = currentIndex.value === null ? undefined : items[currentIndex.value]
    return {
      activeSlot: activeSlot.value,
      currentIndex: currentIndex.value,
      currentItemId: current?.id ?? null,
      expectedIndex: expected?.index ?? null,
      itemCount: playlist.value?.items.length ?? 0,
      scheduledCount: items.length,
      cycleMs: expected?.cycleMs ?? 0,
      transitionMs: transitionMs.value,
      failedIds: [...failedIds],
      recoveries: recoveries.length,
      layers: [getLayer(0)?.status() ?? null, getLayer(1)?.status() ?? null],
    }
  }

  function start(): void {
    if (running) return
    running = true
    document.addEventListener('visibilitychange', onWake)
    window.addEventListener('pageshow', onWake)
    healthTimer = window.setInterval(healthCheck, config.healthIntervalMs)
    enqueue(() => syncToNow(true))
  }

  function stop(): void {
    running = false
    document.removeEventListener('visibilitychange', onWake)
    window.removeEventListener('pageshow', onWake)
    window.clearTimeout(boundaryTimer)
    window.clearTimeout(retireTimer)
    window.clearInterval(healthTimer)
    getLayer(0)?.release()
    getLayer(1)?.release()
  }

  /** Applies a freshly fetched playlist without waiting for the next boundary. */
  function refresh(): void {
    if (!running) return
    preparedIndex = null
    enqueue(() => syncToNow(true))
  }

  return {
    activeSlot,
    retiringSlot,
    transitionMs,
    hasContent,
    start,
    stop,
    refresh,
    snapshot,
  }
}
